import { useEffect, useMemo } from 'react'
import {
  BufferAttribute,
  BufferGeometry,
  CanvasTexture,
  ClampToEdgeWrapping,
  FrontSide,
  LinearFilter,
  MeshBasicMaterial,
  MeshLambertMaterial,
  PlaneGeometry,
  SRGBColorSpace,
} from 'three'

import { useDecorationSpawner, useLevelGeometry, type TerrainMask } from '@hooks'
import { useTexture } from '@react-three/drei'
import {
  buildChunkedMeshes,
  coordHash,
  useGroundControls,
  type ChunkConfig,
  type DecorationConfig,
} from '@utils'

const S = 200
const PLANE_COUNT = 32
const PLANE_SEGMENTS = 2
const GEO_VARIANTS = 3

function createBushShadowTexture(softness: number) {
  const size = 64
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const ctx = canvas.getContext('2d')
  const texture = new CanvasTexture(canvas)
  texture.wrapS = ClampToEdgeWrapping
  texture.wrapT = ClampToEdgeWrapping
  texture.minFilter = LinearFilter
  texture.magFilter = LinearFilter
  texture.generateMipmaps = false

  if (!ctx) return texture

  const gradient = ctx.createRadialGradient(
    size / 2,
    size / 2,
    size * 0.08,
    size / 2,
    size / 2,
    size / 2,
  )
  gradient.addColorStop(0, 'rgb(255, 255, 255)')
  gradient.addColorStop(softness, 'rgb(128, 128, 128)')
  gradient.addColorStop(1, 'rgb(0, 0, 0)')

  ctx.fillStyle = gradient
  ctx.fillRect(0, 0, size, size)

  texture.needsUpdate = true
  return texture
}

// Build one lobe of a bush: Fibonacci quads over a squashed sphere.
function buildLobeGeo(
  seed: number,
  planeCount: number,
  offsetX: number,
  offsetY: number,
  offsetZ: number,
  radiusXZ: number,
  radiusY: number,
  positions: number[],
  normals: number[],
  uvs: number[],
  colors: number[],
  indices: number[],
  normalBlend: number,
) {
  const goldenAngle = Math.PI * (3 - Math.sqrt(5))
  const s = PLANE_SEGMENTS

  for (let p = 0; p < planeCount; p++) {
    const sv = seed + p * 17.3

    const t = p / (planeCount - 1)
    const inclination = Math.acos(1 - 2 * t)
    const azimuth = goldenAngle * p

    const nx = Math.sin(inclination) * Math.cos(azimuth)
    const ny = Math.cos(inclination)
    const nz = Math.sin(inclination) * Math.sin(azimuth)

    // const surfaceBias = p < planeCount * 0.6 ? 0.6 : 0.0
    // const radialDepth = surfaceBias + coordHash(sv + 99, p * 2.1, seed + 41) * (1.0 - surfaceBias)
    const radialDepth = 0.8 + coordHash(sv * 99, p * 2.1, seed * 41) * 0.2

    const cx = offsetX + nx * radiusXZ * radialDepth
    const cy = offsetY + ny * radiusY * radialDepth
    const cz = offsetZ + nz * radiusXZ * radialDepth

    const upX = Math.abs(ny) < 0.9 ? 0 : 1
    const upY = Math.abs(ny) < 0.9 ? 1 : 0
    let tuX = upY * nz
    let tuY = -upX * nz
    let tuZ = upX * ny - upY * nx
    const tuLen = Math.sqrt(tuX * tuX + tuY * tuY + tuZ * tuZ) || 1
    tuX /= tuLen
    tuY /= tuLen
    tuZ /= tuLen
    const tvX = ny * tuZ - nz * tuY
    const tvY = nz * tuX - nx * tuZ
    const tvZ = nx * tuY - ny * tuX

    const hw = 0.18 + coordHash(sv, p + 2, seed + 5) * 0.1
    const hh = 0.18 + coordHash(sv, p + 3, seed + 6) * 0.1
    // [0.7, 1.2] — values above 1 give occasional bright highlights
    const brightness = 0.7 + coordHash(sv, p + 7, seed + 9) * 0.5
    // const frontFacesOutward = nz >= 0

    const indexBase = positions.length / 3

    for (let row = 0; row <= s; row++) {
      const tv = row / s
      const localV = (tv - 0.5) * 2 * hh
      for (let col = 0; col <= s; col++) {
        const tu = col / s
        const localU = (tu - 0.5) * 2 * hw
        positions.push(
          cx + tuX * localU + tvX * localV,
          cy + tuY * localU + tvY * localV,
          cz + tuZ * localU + tvZ * localV,
        )
        const bnx = nx * normalBlend
        const bny = 1 - normalBlend + ny * normalBlend
        const bnz = nz * normalBlend
        const bLen = Math.sqrt(bnx * bnx + bny * bny + bnz * bnz)
        normals.push(bnx / bLen, bny / bLen, bnz / bLen)
        uvs.push(tu, tv)
        colors.push(brightness, brightness, brightness)
      }
    }

    for (let row = 0; row < s; row++) {
      for (let col = 0; col < s; col++) {
        const a = indexBase + row * (s + 1) + col
        const b = a + 1
        const c = a + (s + 1)
        const d = c + 1
        // if (frontFacesOutward) {
        indices.push(a, b, c, b, d, c)
        // } else {
        //   indices.push(a, c, b, b, c, d)
        // }
      }
    }
  }
}

// Bush = 2-3 overlapping lobes of different sizes, simulating irregular shape.
function buildBushGeo(seed: number, normalBlend: number): BufferGeometry {
  const positions: number[] = []
  const normals: number[] = []
  const uvs: number[] = []
  const colors: number[] = []
  const indices: number[] = []

  // Main lobe
  buildLobeGeo(
    seed,
    PLANE_COUNT,
    0,
    0.28,
    0,
    0.42,
    0.3,
    positions,
    normals,
    uvs,
    colors,
    indices,
    normalBlend,
  )

  // Secondary lobe: smaller, offset to one side
  const angle2 = coordHash(seed, 3.7, 11.1) * Math.PI * 2
  const dist2 = 0.22 + coordHash(seed, 5.1, 7.3) * 0.1
  const ox2 = Math.cos(angle2) * dist2
  const oz2 = Math.sin(angle2) * dist2
  const scale2 = 0.55 + coordHash(seed, 9.3, 2.7) * 0.2
  buildLobeGeo(
    seed + 500,
    Math.floor(PLANE_COUNT * 0.6),
    ox2,
    0.22,
    oz2,
    0.42 * scale2,
    0.3 * scale2,
    positions,
    normals,
    uvs,
    colors,
    indices,
    normalBlend,
  )

  // Optional third lobe (~60% chance): even smaller, opposite side
  if (coordHash(seed, 13.1, 19.7) > 0.4) {
    const angle3 = angle2 + Math.PI * (0.6 + coordHash(seed, 17.3, 23.9) * 0.8)
    const dist3 = 0.18 + coordHash(seed, 21.1, 6.3) * 0.1
    const ox3 = Math.cos(angle3) * dist3
    const oz3 = Math.sin(angle3) * dist3
    const scale3 = 0.4 + coordHash(seed, 25.7, 4.1) * 0.2
    buildLobeGeo(
      seed + 1000,
      Math.floor(PLANE_COUNT * 0.45),
      ox3,
      0.2,
      oz3,
      0.42 * scale3,
      0.3 * scale3,
      positions,
      normals,
      uvs,
      colors,
      indices,
      normalBlend,
    )
  }

  const geo = new BufferGeometry()
  geo.setAttribute('position', new BufferAttribute(new Float32Array(positions), 3))
  geo.setAttribute('normal', new BufferAttribute(new Float32Array(normals), 3))
  geo.setAttribute('uv', new BufferAttribute(new Float32Array(uvs), 2))
  geo.setAttribute('color', new BufferAttribute(new Float32Array(colors), 3))
  geo.setIndex(indices)
  geo.computeBoundingSphere()
  return geo
}

interface BushesProps {
  mask: TerrainMask
  full?: boolean
}

export function Bushes({ mask, full = true }: BushesProps) {
  const {
    normalBlend,
    bushShadow,
    bushShadowOpacity,
    bushShadowSoftness,
    bushShadowSize,
    bushShadowPosition,
  } = useGroundControls()

  const { size } = useLevelGeometry()
  const frontZ = size.height / 2
  const leavesTexture = useTexture('/textures/leaves.png', t => {
    t.colorSpace = SRGBColorSpace
    t.flipY = true
  })

  const config = useMemo<DecorationConfig>(
    () => ({
      cellSize: 0.5,
      minTrackDist: 1.1,
      minSpacing: 0.1,
      density: 0.6,
      allow: (wx, wz, m) => (full || wz >= -frontZ) && m.sampleDarkGreen(wx, wz),
      score: (wx, wz) => coordHash(wx, wz, S),
    }),
    [frontZ, full],
  )

  const geos = useMemo(
    () => Array.from({ length: GEO_VARIANTS }, (_, vi) => buildBushGeo(vi * 137.5, normalBlend)),
    [normalBlend],
  )

  useEffect(() => {
    return () => {
      geos.forEach(g => g.dispose())
    }
  }, [geos])

  const chunkCfg = useMemo<ChunkConfig>(
    () => ({ side: mask.side, centerOffset: mask.centerOffset, chunksX: 4, chunksZ: 4 }),
    [mask],
  )

  const instances = useDecorationSpawner(mask, config, GEO_VARIANTS, S, 0.4, 0.55)

  const material = useMemo(
    () =>
      new MeshLambertMaterial({
        map: leavesTexture,
        vertexColors: true,
        alphaTest: 0.4,
        transparent: false,
        depthWrite: true,
        side: FrontSide,
      }),
    [leavesTexture],
  )

  const bushShadowTexture = useMemo(
    () => createBushShadowTexture(bushShadowSoftness),
    [bushShadowSoftness],
  )

  const shadowGeo = useMemo(() => {
    const geo = new PlaneGeometry(bushShadowSize, bushShadowSize)
    geo.rotateX(-Math.PI / 2)
    geo.translate(...bushShadowPosition)
    return geo
  }, [bushShadowSize, bushShadowPosition])

  const shadowMaterial = useMemo(
    () =>
      new MeshBasicMaterial({
        color: 0x000000,
        alphaMap: bushShadowTexture,
        transparent: true,
        opacity: bushShadowOpacity,
        depthWrite: false,
      }),
    [bushShadowOpacity, bushShadowTexture],
  )

  const meshes = useMemo(() => {
    return instances.flatMap((insts, vi) => {
      if (!insts.length) return []
      return buildChunkedMeshes(geos[vi], material, insts, chunkCfg, undefined)
    })
  }, [chunkCfg, geos, instances, material])

  const shadowInstances = useMemo(() => {
    if (!bushShadow) return []
    return instances.map(insts =>
      insts.map(inst => ({
        ...inst,
        wx: inst.wx + bushShadowPosition[0],
        wz: inst.wz + bushShadowPosition[2],
      })),
    )
  }, [bushShadow, bushShadowPosition, instances])

  const shadowMeshes = useMemo(() => {
    if (!shadowInstances.length) return []

    const meshes = shadowInstances.flatMap(insts => {
      if (!insts.length) return []
      return buildChunkedMeshes(shadowGeo, shadowMaterial, insts, chunkCfg, undefined)
    })

    meshes.forEach(mesh => {
      mesh.receiveShadow = false
      mesh.renderOrder = -1
    })

    return meshes
  }, [chunkCfg, shadowGeo, shadowInstances, shadowMaterial])

  useEffect(() => {
    return () => {
      material.dispose()
      bushShadowTexture.dispose()
      shadowGeo.dispose()
      shadowMaterial.dispose()
    }
  }, [bushShadowTexture, material, shadowGeo, shadowMaterial])

  useEffect(() => {
    return () => {
      meshes.forEach(mesh => mesh.geometry.dispose())
      shadowMeshes.forEach(mesh => mesh.geometry.dispose())
    }
  }, [meshes, shadowMeshes])

  return [
    ...shadowMeshes.map((mesh, i) => <primitive key={`shadow-${i}`} object={mesh} />),
    ...meshes.map((mesh, i) => <primitive key={`bush-${i}`} object={mesh} />),
  ]
}
